interface HarmonyHubLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'full' | 'icon' | 'text'
  className?: string
}

export default function HarmonyHubLogo({ 
  size = 'md', 
  variant = 'full',
  className = '' 
}: HarmonyHubLogoProps) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  }

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl',
    xl: 'text-2xl'
  }

  if (variant === 'text') {
    return (
      <span className={`font-heading font-bold text-gold-500 ${textSizeClasses[size]} ${className}`}>
        Harmony Hub
      </span>
    )
  }

  if (variant === 'icon') {
    return (
      <div className={`${sizeClasses[size]} ${className}`}>
        <svg
          viewBox="0 0 64 64"
          className="w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer ring with emerald green accents */}
          <circle
            cx="32"
            cy="32"
            r="30"
            fill="none"
            stroke="url(#emeraldGradient)"
            strokeWidth="2"
            opacity="0.3"
          />
          
          {/* Main crest shape */}
          <path
            d="M32 8 L48 20 L48 44 L32 56 L16 44 L16 20 Z"
            fill="url(#crestGradient)"
            stroke="url(#goldPurpleGradient)"
            strokeWidth="2"
          />
          
          {/* Interlocked HH letters */}
          <g fill="url(#letterGradient)">
            {/* H letter 1 */}
            <path d="M24 22 L24 42 M24 32 L28 32 M28 22 L28 42" stroke="url(#letterGradient)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            {/* H letter 2 */}
            <path d="M36 22 L36 42 M36 32 L40 32 M40 22 L40 42" stroke="url(#letterGradient)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </g>
          
          {/* Subtle purple glow background */}
          <circle
            cx="32"
            cy="32"
            r="31"
            fill="none"
            stroke="url(#purpleGlow)"
            strokeWidth="1"
            opacity="0.4"
          />
          
          {/* Gradients */}
          <defs>
            <linearGradient id="crestGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="50%" stopColor="#6A0DAD" />
              <stop offset="100%" stopColor="#3B82F6" />
            </linearGradient>
            
            <linearGradient id="goldPurpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#6A0DAD" />
            </linearGradient>
            
            <linearGradient id="letterGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#3B82F6" />
            </linearGradient>
            
            <radialGradient id="emeraldGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0.2" />
            </radialGradient>
            
            <radialGradient id="purpleGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#6A0DAD" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#6A0DAD" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>
    )
  }

  // Full variant (icon + text)
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={sizeClasses[size]}>
        <svg
          viewBox="0 0 64 64"
          className="w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer ring with emerald green accents */}
          <circle
            cx="32"
            cy="32"
            r="30"
            fill="none"
            stroke="url(#emeraldGradient)"
            strokeWidth="2"
            opacity="0.3"
          />
          
          {/* Main crest shape */}
          <path
            d="M32 8 L48 20 L48 44 L32 56 L16 44 L16 20 Z"
            fill="url(#crestGradient)"
            stroke="url(#goldPurpleGradient)"
            strokeWidth="2"
          />
          
          {/* Interlocked HH letters */}
          <g fill="url(#letterGradient)">
            {/* H letter 1 */}
            <path d="M24 22 L24 42 M24 32 L28 32 M28 22 L28 42" stroke="url(#letterGradient)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            {/* H letter 2 */}
            <path d="M36 22 L36 42 M36 32 L40 32 M40 22 L40 42" stroke="url(#letterGradient)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </g>
          
          {/* Subtle purple glow background */}
          <circle
            cx="32"
            cy="32"
            r="31"
            fill="none"
            stroke="url(#purpleGlow)"
            strokeWidth="1"
            opacity="0.4"
          />
          
          {/* Gradients */}
          <defs>
            <linearGradient id="crestGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="50%" stopColor="#6A0DAD" />
              <stop offset="100%" stopColor="#3B82F6" />
            </linearGradient>
            
            <linearGradient id="goldPurpleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#6A0DAD" />
            </linearGradient>
            
            <linearGradient id="letterGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#D4AF37" />
              <stop offset="100%" stopColor="#3B82F6" />
            </linearGradient>
            
            <radialGradient id="emeraldGradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0.2" />
            </radialGradient>
            
            <radialGradient id="purpleGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#6A0DAD" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#6A0DAD" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>
      <span className={`font-heading font-bold text-gold-500 ${textSizeClasses[size]}`}>
        Harmony Hub
      </span>
    </div>
  )
}
